import { isAutoTransport } from 'lib/autoTransport/isAutoTransport';

import { CalculationStep, MarginBasis, SourceRow, Suggestion } from '../types';
import { formatMoney, formatSuggestedMargin } from './format';

export enum CalculationStepId {
  CheckConfidence = 'check_confidence',
  AverageSources = 'average_sources',
  ApplyMargin = 'apply_margin',
}

type BuildCalculationStepsArgs = {
  suggestion: Suggestion;
  profitPercentLabel: string;
  formatMoney?: (value: number) => string;
};

// Matches backend low confidence threshold default
export const CONFIDENCE_THRESHOLD = 80;

export const shortSourceLabel = (label: string): string => {
  if (label.includes('DAT')) {
    return 'DAT';
  }
  if (label.includes('Triumph')) {
    return 'Triumph';
  }
  if (label.includes('SONAR')) {
    return 'SONAR';
  }
  return label;
};

// Backend sets isLowConfidence when a source is excluded from the Kit average,
// unless every source was low confidence and Kit averaged them anyway.
export function isSourceIncludedInAverage(
  source: SourceRow,
  isLowConfidenceQuote = false
): boolean {
  return isLowConfidenceQuote || source.isLowConfidence !== true;
}

const formatSourceBuys = (
  sources: SourceRow[],
  formatMoneyFn: (value: number) => string
): string => {
  return sources
    .map((source) => {
      return `${shortSourceLabel(source.label)} ${formatMoneyFn(source.buy)}`;
    })
    .join(' and ');
};

const joinNaturalList = (parts: string[]): string => {
  switch (parts.length) {
    case 0:
      return '';
    case 1:
      return parts[0];
    case 2:
      return `${parts[0]} and ${parts[1]}`;
    default:
      return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }
};

const sourcesWithConfidence = (
  sources: SourceRow[]
): Array<SourceRow & { confidenceLevel: number }> => {
  return sources.filter(
    (source): source is SourceRow & { confidenceLevel: number } => {
      return typeof source.confidenceLevel === 'number';
    }
  );
};

const formatIncludedConfidence = (
  scoredSources: Array<SourceRow & { confidenceLevel: number }>
): string => {
  const parts = scoredSources.map((source) => {
    const label = shortSourceLabel(source.label);
    return `${label} at ${source.confidenceLevel}% confidence`;
  });

  return `Included ${joinNaturalList(parts)} (≥${CONFIDENCE_THRESHOLD}%).`;
};

const formatExcludedConfidence = (sources: SourceRow[]): string => {
  if (sources.length === 0) {
    return '';
  }

  const formatted = sources.map((source) => {
    const label = shortSourceLabel(source.label);
    if (typeof source.confidenceLevel === 'number') {
      return `${label} (${source.confidenceLevel}%)`;
    }
    return label;
  });

  return `Filtered out sources below confidence threshold of ${CONFIDENCE_THRESHOLD}%: ${joinNaturalList(formatted)}.`;
};

const formatAverageSourcesDescription = (
  includedSources: SourceRow[],
  formatMoneyFn: (value: number) => string
): string => {
  if (includedSources.length === 0) {
    return 'Averaged live rating sources into the buy rate.';
  }

  const sourceBuys = formatSourceBuys(includedSources, formatMoneyFn);

  if (includedSources.length === 1) {
    return `Used ${sourceBuys} as the buy rate.`;
  }

  return `Calculated average from ${sourceBuys} into the buy rate.`;
};

export const formatSourceConfidenceList = (sources: SourceRow[]): string =>
  joinNaturalList(
    sources.map((source) => {
      const label = shortSourceLabel(source.label);
      return typeof source.confidenceLevel === 'number'
        ? `${label} (${source.confidenceLevel}%)`
        : label;
    })
  );

const formatLowConfidenceDescription = (sources: SourceRow[]): string => {
  const usedPronoun = sources.length === 1 ? 'it' : 'them';
  return `Every source is below the ${CONFIDENCE_THRESHOLD}% confidence threshold: ${formatSourceConfidenceList(sources)}. Kit used ${usedPronoun} anyway, so treat the buy rate as a starting point.`;
};

const formatApplyMarginDescription = (
  suggestion: Suggestion,
  profitPercentLabel: string,
  formatMoneyFn: (value: number) => string
): string => {
  const margin = `${formatSuggestedMargin(suggestion, formatMoneyFn)} ${profitPercentLabel}`;

  switch (suggestion.marginBasis) {
    case MarginBasis.UserDefault:
      return `Applied your default ${margin}.`;
    case MarginBasis.Configured:
      return `Applied your company's default ${margin}.`;
    case MarginBasis.Fallback:
      return `Applied Kit's standard ${margin}, since no default is set.`;
    default:
      return `Applied the default ${margin}.`;
  }
};

const formatConfidenceDescription = (
  includedScored: Array<SourceRow & { confidenceLevel: number }>,
  excludedSources: SourceRow[]
): string => {
  const parts: string[] = [];

  if (includedScored.length > 0) {
    parts.push(formatIncludedConfidence(includedScored));
  }

  const excludedMessage = formatExcludedConfidence(excludedSources);
  if (excludedMessage) {
    parts.push(excludedMessage);
  }

  return parts.join(' ');
};

// Builds retrospective calculation steps for the live Kit Coach card.
// Steps describe work the backend already completed before the quote returned.
export function buildCalculationSteps({
  suggestion,
  profitPercentLabel,
  formatMoney: formatMoneyOverride,
}: BuildCalculationStepsArgs): CalculationStep[] {
  if (isAutoTransport(suggestion.transportType)) {
    // The backend writes the auto transport steps. If it sends none, show none:
    // the freight averaging steps below don't describe how auto is priced.
    return (suggestion.calculationSteps ?? []).filter(
      isCompleteCalculationStep
    );
  }

  const formatMoneyFn = formatMoneyOverride ?? formatMoney;
  const isLowConfidenceQuote = suggestion.isLowConfidence === true;
  const includedSources = suggestion.sources.filter((source) =>
    isSourceIncludedInAverage(source, isLowConfidenceQuote)
  );
  const excludedSources = suggestion.sources.filter(
    (source) => !isSourceIncludedInAverage(source, isLowConfidenceQuote)
  );
  const includedScored = sourcesWithConfidence(includedSources);
  const steps: CalculationStep[] = [];

  if (isLowConfidenceQuote) {
    steps.push({
      id: CalculationStepId.CheckConfidence,
      title: 'Check source confidence',
      description: formatLowConfidenceDescription(suggestion.sources),
    });
  } else if (includedScored.length > 0 || excludedSources.length > 0) {
    steps.push({
      id: CalculationStepId.CheckConfidence,
      title: 'Check source confidence',
      description: formatConfidenceDescription(includedScored, excludedSources),
    });
  }

  steps.push(
    {
      id: CalculationStepId.AverageSources,
      title: 'Average price sources',
      description: formatAverageSourcesDescription(
        includedSources,
        formatMoneyFn
      ),
    },
    {
      id: CalculationStepId.ApplyMargin,
      title: `Apply ${profitPercentLabel}`,
      description: formatApplyMarginDescription(
        suggestion,
        profitPercentLabel,
        formatMoneyFn
      ),
    }
  );

  return steps;
}

// Backend payloads can be malformed at runtime despite the static type.
function isCompleteCalculationStep(step: CalculationStep): boolean {
  return (
    typeof step.id === 'string' &&
    step.id.length > 0 &&
    typeof step.title === 'string' &&
    typeof step.description === 'string'
  );
}
